import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import AppLayout from '@/components/layout/AppLayout.vue'
import HomeView from '@/views/HomeView.vue'
import AboutView from '@/views/AboutView.vue'
import LoginView from '@/views/LoginView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import HomeIcon from '@/components/icons/HomeIcon.vue'
import AboutIcon from '@/components/icons/AboutIcon.vue'
import BrandSelectionView from '@/views/BrandSelectionView.vue'
import SignUpView from '@/views/SignUpView.vue'
import ForgotPasswordView from '@/views/ForgotPasswordView.vue'
import ResetPasswordView from '@/views/ResetPasswordView.vue'
import EmailVerificationView from '@/views/EmailVerificationView.vue'

// Customer Views
import CustomerInfoView from '@/views/customer/CustomerInfoView.vue'
import VehicleInfoView from '@/views/customer/VehicleInfoView.vue'

// Admin Views
import AdminLoginView from '@/views/admin/AdminLoginView.vue'
import DashboardView from '@/views/admin/DashboardView.vue'
import CustomersView from '@/views/admin/CustomersView.vue'
import ClaimsView from '@/views/admin/ClaimsView.vue'
import TireApprovalsView from '@/views/admin/TireApprovalsView.vue'
import TcpInfoView from '@/views/admin/TcpInfoView.vue'

// Types
interface NavigationItem {
  name: string
  to: string
  icon: any
}

interface RouteMeta {
  requiresAuth?: boolean
  title?: string
  icon?: any
  roles?: string[] // Add roles field for role-based access
  brand?: string // Add brand field for brand-specific tabs
}

// Route Configuration
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/brands'
  },
  {
    path: '/brands',
    name: 'brand-selection',
    component: BrandSelectionView,
    meta: {
      requiresAuth: false,
      title: 'Select Brand'
    }
  },
  // Sign Up (Brand-Specific)
  {
    path: '/signup/:brandSlug',
    name: 'signup',
    component: SignUpView,
    meta: {
      requiresAuth: false,
      title: 'Sign Up'
    }
  },
  // Login (Brand-Specific)
  {
    path: '/login/:brandSlug',
    name: 'login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      title: 'Login'
    }
  },
  // Forgot Password (Brand-Specific)
  {
    path: '/forgot-password/:brandSlug',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: {
      requiresAuth: false,
      title: 'Forgot Password'
    }
  },
  // Reset Password (No brand needed - token-based)
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: {
      requiresAuth: false,
      title: 'Reset Password'
    }
  },
  // Email Verification (Token-based)
  {
    path: '/verify-email',
    name: 'verify-email',
    component: EmailVerificationView,
    meta: {
      requiresAuth: false,
      title: 'Verify Email'
    }
  },
  // Admin Login (Separate)
  {
    path: '/admin/login',
    name: 'admin-login',
    component: AdminLoginView,
    meta: {
      requiresAuth: false,
      title: 'Admin Login'
    }
  },
  // Protected Routes - Customer Dashboard
  {
    path: '/dashboard',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'tcp',
        component: HomeView,
        meta: {
          requiresAuth: true,
          title: 'What is TCP?',
          icon: HomeIcon,
          roles: ['customer']
        }
      },
      {
        path: 'customer-info',
        name: 'customer-info',
        component: CustomerInfoView,
        meta: {
          requiresAuth: true,
          title: 'Customer Info',
          icon: HomeIcon,
          roles: ['customer']
        }
      },
      {
        path: 'vehicles',
        name: 'vehicles',
        component: VehicleInfoView,
        meta: {
          requiresAuth: true,
          title: 'Vehicle Info',
          icon: AboutIcon,
          roles: ['customer']
        }
      },
      {
        path: 'tires',
        name: 'tires',
        component: () => import('@/views/customer/TireRegistrationView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Register My Tires',
          icon: HomeIcon,
          roles: ['customer']
        }
      },
      {
        path: 'claim',
        name: 'claim',
        component: () => import('@/views/customer/ClaimView.vue'),
        meta: {
          requiresAuth: true,
          title: 'My Claims',
          icon: AboutIcon,
          roles: ['customer']
        }
      },
      {
        path: 'know-more-tcp',
        name: 'know-more-tcp',
        component: () => import('@/views/customer/KnowMoreTcpView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Know More About TCP',
          icon: AboutIcon,
          roles: ['customer'],
          brand: 'continental'
        }
      },
      // Admin Routes
      {
        path: 'admin',
        name: 'admin-dashboard',
        component: DashboardView,
        meta: {
          requiresAuth: true,
          title: 'Dashboard',
          icon: HomeIcon,
          roles: ['admin']
        }
      },
      {
        path: 'admin/customers',
        name: 'admin-customers',
        component: CustomersView,
        meta: {
          requiresAuth: true,
          title: 'Customers',
          icon: AboutIcon,
          roles: ['admin']
        }
      },
      {
        path: 'admin/tire-approvals',
        name: 'admin-tire-approvals',
        component: TireApprovalsView,
        meta: {
          requiresAuth: true,
          title: 'Tire Approvals',
          icon: AboutIcon,
          roles: ['admin']
        }
      },
      {
        path: 'admin/claims',
        name: 'admin-claims',
        component: ClaimsView,
        meta: {
          requiresAuth: true,
          title: 'Claims',
          icon: HomeIcon,
          roles: ['admin']
        }
      },
      // {
      //   path: 'admin/tcp-info',
      //   name: 'admin-tcp-info',
      //   component: TcpInfoView,
      //   meta: {
      //     requiresAuth: true,
      //     title: 'TCP Info',
      //     icon: AboutIcon,
      //     roles: ['admin']
      //   }
      // },
    ]
  },
  // Error Pages
  {
    path: '/500',
    name: 'server-error',
    component: () => import('@/views/ServerErrorView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: {
      requiresAuth: false,
      title: 'Not Found'
    }
  }
]

// Router Instance
const router = createRouter({
  history: createWebHistory(process.env.VUE_APP_BASE_URL),
  routes
})

// Navigation Helper - Role-Based
export const getNavigationItems = (routes: RouteRecordRaw[], userRole?: string, userBrandSlug?: string): NavigationItem[] => {
  const layoutRoute = routes.find(route => route.path === '/dashboard')
  if (!layoutRoute?.children) return []

  return layoutRoute.children
    .filter(route => {
      const meta = route.meta as RouteMeta
      // Filter by auth requirement
      if (!meta?.requiresAuth) return false
      // Filter by role if specified
      if (userRole && meta.roles && !meta.roles.includes(userRole)) return false
      // Filter by brand if specified
      if (userBrandSlug && meta.brand && meta.brand !== userBrandSlug) return false
      return true
    })
    .map(route => ({
      name: (route.meta as RouteMeta)?.title || String(route.name),
      to: route.path ? `/dashboard/${route.path}` : '/dashboard',
      icon: (route.meta as RouteMeta)?.icon
    }))
}

// Navigation Guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Check if route requires authentication
  if (requiresAuth) {
    // Check if user is authenticated
    const isAuthenticated = await authStore.checkAuth()

    if (!isAuthenticated) {
      // Redirect to brand selection if not authenticated
      next({ name: 'brand-selection' })
      return
    }

    // Check role-based access
    const routeMeta = to.meta as RouteMeta
    const userRole = authStore.currentUser?.role

    if (routeMeta.roles && userRole && !routeMeta.roles.includes(userRole)) {
      // If admin is in customer view mode, allow customer routes through
      if (userRole === 'admin' && authStore.isAdminViewMode && routeMeta.roles.includes('customer')) {
        next()
        return
      }

      // Redirect to appropriate dashboard based on role
      if (userRole === 'admin') {
        next({ name: 'admin-dashboard' })
      } else {
        next({ name: 'tcp' })
      }
      return
    }
  }

  // If user is authenticated and trying to access auth pages
  if ((to.name === 'login' || to.name === 'signup' || to.name === 'brand-selection' || to.name === 'admin-login') && authStore.isAuthenticated) {
    const userRole = authStore.currentUser?.role
    // Redirect based on role
    if (userRole === 'admin') {
      next({ name: 'admin-dashboard' })
    } else {
      next({ name: 'tcp' })
    }
    return
  }

  next()
})

// Update page title after each navigation
router.afterEach((to) => {
  const baseTitle = process.env.VUE_APP_TITLE || 'JM Far East, Inc.'
  const routeTitle = to.meta.title as string | undefined

  // Set title: "Page Title - Base Title" or just "Base Title"
  document.title = routeTitle ? `${routeTitle} - ${baseTitle}` : baseTitle
})

export default router